/**
 * Tone.js-backed effect plugins.
 *
 * Each export conforms to the generic `EffectPlugin` contract in
 * `audio/plugins/types.ts` so they're drop-in equivalents to the
 * hand-rolled Web Audio factories in `audio/effects.ts`. Call
 * `registerToneEffects()` once at boot to get them into the
 * registry — idempotent so repeated calls are safe.
 *
 * Chorus + Compressor + Limiter + EQ3 cover gaps the existing
 * effect bank doesn't fill:
 *   - `tone-chorus`     → fills CC 93 in the MIDI gap analysis.
 *   - `tone-compressor` → master dynamics.
 *   - `tone-limiter`    → master-bus peak limiter (export clip guard).
 *   - `tone-eq3`        → 3-band EQ as a reusable plugin.
 *
 * The hand-rolled reverb / delay / bitcrush / stutter stay on their
 * Web Audio implementations because those have subtle tuning we
 * don't want to replace wholesale yet. Future PRs can migrate them
 * behind the same registry id if Tone's surface turns out to win.
 */

import {
  Chorus,
  Compressor,
  Limiter,
  EQ3,
  AutoFilter,
  AutoWah,
  Phaser,
  Tremolo,
  Vibrato,
  PitchShift,
  StereoWidener,
  Gain as ToneGain,
  type ToneAudioNode,
} from 'tone'
import { registerEffect } from '@/audio/plugins/registry'
import type { EffectPlugin, PluginHandle } from '@/audio/plugins/types'
import { registerToneContext } from './registerContext'

let registered = false

export function registerToneEffects(): void {
  if (registered) return
  registered = true

  registerEffect(chorusPlugin)
  registerEffect(compressorPlugin)
  registerEffect(limiterPlugin)
  registerEffect(eq3Plugin)
  // Second batch — PLAN follow-up P9. Modulation + utility
  // plugins that fill out the effect library without changing
  // any host behaviour. All share the same `EffectPlugin` shape
  // so the existing picker UI discovers them automatically.
  registerEffect(autoFilterPlugin)
  registerEffect(autoWahPlugin)
  registerEffect(phaserPlugin)
  registerEffect(tremoloPlugin)
  registerEffect(vibratoPlugin)
  registerEffect(pitchShiftPlugin)
  registerEffect(stereoWidenerPlugin)
}

const chorusPlugin: EffectPlugin = {
  id: 'tone-chorus',
  label: 'Chorus',
  category: 'modulation',
  defaultPreset: { frequency: 1.5, delayTime: 3.5, depth: 0.4, wet: 0.5 },
  paramSchema: [
    {
      id: 'frequency',
      label: 'Rate',
      kind: 'slider',
      min: 0.05,
      max: 10,
      step: 0.05,
      default: 1.5,
    },
    {
      id: 'delayTime',
      label: 'Delay',
      kind: 'time',
      min: 0.5,
      max: 20,
      step: 0.1,
      default: 3.5,
    },
    {
      id: 'depth',
      label: 'Depth',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.4,
    },
    {
      id: 'wet',
      label: 'Wet',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
    },
  ],
  createNode: (ctx, preset = {}) => {
    registerToneContext(ctx)
    const chorus = new Chorus(
      preset.frequency ?? 1.5,
      preset.delayTime ?? 3.5,
      preset.depth ?? 0.4,
    ).start()
    chorus.wet.value = preset.wet ?? 0.5
    return wrapToneEffect(chorus, {
      frequency: v => (chorus.frequency.value = v),
      delayTime: v => (chorus.delayTime = v),
      depth: v => (chorus.depth = v),
      wet: v => (chorus.wet.value = v),
    })
  },
}

const compressorPlugin: EffectPlugin = {
  id: 'tone-compressor',
  label: 'Compressor',
  category: 'dynamics',
  defaultPreset: {
    threshold: -24,
    ratio: 4,
    attack: 0.003,
    release: 0.25,
    knee: 6,
  },
  paramSchema: [
    {
      id: 'threshold',
      label: 'Threshold',
      kind: 'slider',
      min: -60,
      max: 0,
      step: 0.5,
      default: -24,
    },
    {
      id: 'ratio',
      label: 'Ratio',
      kind: 'slider',
      min: 1,
      max: 20,
      step: 0.1,
      default: 4,
    },
    {
      id: 'attack',
      label: 'Attack',
      kind: 'time',
      min: 0,
      max: 1,
      step: 0.001,
      default: 0.003,
    },
    {
      id: 'release',
      label: 'Release',
      kind: 'time',
      min: 0,
      max: 2,
      step: 0.005,
      default: 0.25,
    },
    {
      id: 'knee',
      label: 'Knee',
      kind: 'slider',
      min: 0,
      max: 40,
      step: 0.5,
      default: 6,
    },
  ],
  createNode: (ctx, preset = {}) => {
    registerToneContext(ctx)
    const comp = new Compressor({
      threshold: preset.threshold ?? -24,
      ratio: preset.ratio ?? 4,
      attack: preset.attack ?? 0.003,
      release: preset.release ?? 0.25,
      knee: preset.knee ?? 6,
    })
    return wrapToneEffect(comp, {
      threshold: v => (comp.threshold.value = v),
      ratio: v => (comp.ratio.value = v),
      attack: v => (comp.attack.value = v),
      release: v => (comp.release.value = v),
      knee: v => (comp.knee.value = v),
    })
  },
}

const limiterPlugin: EffectPlugin = {
  id: 'tone-limiter',
  label: 'Limiter',
  category: 'dynamics',
  defaultPreset: { threshold: -3 },
  paramSchema: [
    {
      id: 'threshold',
      label: 'Threshold',
      kind: 'slider',
      min: -24,
      max: 0,
      step: 0.1,
      default: -3,
    },
  ],
  createNode: (ctx, preset = {}) => {
    registerToneContext(ctx)
    const limiter = new Limiter(preset.threshold ?? -3)
    return wrapToneEffect(limiter, {
      threshold: v => (limiter.threshold.value = v),
    })
  },
}

const eq3Plugin: EffectPlugin = {
  id: 'tone-eq3',
  label: 'EQ 3-band',
  category: 'eq',
  defaultPreset: {
    low: 0,
    mid: 0,
    high: 0,
    lowFrequency: 400,
    highFrequency: 2500,
  },
  paramSchema: [
    {
      id: 'low',
      label: 'Low',
      kind: 'slider',
      min: -24,
      max: 12,
      step: 0.5,
      default: 0,
    },
    {
      id: 'mid',
      label: 'Mid',
      kind: 'slider',
      min: -24,
      max: 12,
      step: 0.5,
      default: 0,
    },
    {
      id: 'high',
      label: 'High',
      kind: 'slider',
      min: -24,
      max: 12,
      step: 0.5,
      default: 0,
    },
    {
      id: 'lowFrequency',
      label: 'Low xo',
      kind: 'freq',
      min: 20,
      max: 2000,
      step: 10,
      default: 400,
    },
    {
      id: 'highFrequency',
      label: 'High xo',
      kind: 'freq',
      min: 200,
      max: 20000,
      step: 50,
      default: 2500,
    },
  ],
  createNode: (ctx, preset = {}) => {
    registerToneContext(ctx)
    const eq = new EQ3({
      low: preset.low ?? 0,
      mid: preset.mid ?? 0,
      high: preset.high ?? 0,
      lowFrequency: preset.lowFrequency ?? 400,
      highFrequency: preset.highFrequency ?? 2500,
    })
    return wrapToneEffect(eq, {
      low: v => (eq.low.value = v),
      mid: v => (eq.mid.value = v),
      high: v => (eq.high.value = v),
      lowFrequency: v => (eq.lowFrequency.value = v),
      highFrequency: v => (eq.highFrequency.value = v),
    })
  },
}

/** ───── P9 second batch ─────────────────────────────────────── */

const autoFilterPlugin: EffectPlugin = {
  id: 'tone-autofilter',
  label: 'Auto-Filter',
  category: 'modulation',
  defaultPreset: { frequency: 1, baseFrequency: 400, octaves: 2.6, wet: 0.8 },
  paramSchema: [
    {
      id: 'frequency',
      label: 'LFO Rate',
      kind: 'slider',
      min: 0.05,
      max: 20,
      step: 0.05,
      default: 1,
    },
    {
      id: 'baseFrequency',
      label: 'Base freq',
      kind: 'freq',
      min: 20,
      max: 8000,
      step: 10,
      default: 400,
    },
    {
      id: 'octaves',
      label: 'Octaves',
      kind: 'slider',
      min: 0,
      max: 8,
      step: 0.1,
      default: 2.6,
    },
    {
      id: 'wet',
      label: 'Wet',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.8,
    },
  ],
  createNode: (ctx, preset = {}) => {
    registerToneContext(ctx)
    const af = new AutoFilter({
      frequency: preset.frequency ?? 1,
      baseFrequency: preset.baseFrequency ?? 400,
      octaves: preset.octaves ?? 2.6,
    }).start()
    af.wet.value = preset.wet ?? 0.8
    return wrapToneEffect(af, {
      frequency: v => (af.frequency.value = v),
      baseFrequency: v => (af.baseFrequency = v),
      octaves: v => (af.octaves = v),
      wet: v => (af.wet.value = v),
    })
  },
}

const autoWahPlugin: EffectPlugin = {
  id: 'tone-autowah',
  label: 'Auto-Wah',
  category: 'modulation',
  defaultPreset: { baseFrequency: 100, octaves: 6, sensitivity: 0, wet: 0.5 },
  paramSchema: [
    {
      id: 'baseFrequency',
      label: 'Base freq',
      kind: 'freq',
      min: 20,
      max: 2000,
      step: 1,
      default: 100,
    },
    {
      id: 'octaves',
      label: 'Octaves',
      kind: 'slider',
      min: 0,
      max: 10,
      step: 0.1,
      default: 6,
    },
    {
      id: 'sensitivity',
      label: 'Sensitivity',
      kind: 'slider',
      min: -40,
      max: 0,
      step: 0.5,
      default: 0,
    },
    {
      id: 'wet',
      label: 'Wet',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
    },
  ],
  createNode: (ctx, preset = {}) => {
    registerToneContext(ctx)
    const aw = new AutoWah({
      baseFrequency: preset.baseFrequency ?? 100,
      octaves: preset.octaves ?? 6,
      sensitivity: preset.sensitivity ?? 0,
    })
    aw.wet.value = preset.wet ?? 0.5
    return wrapToneEffect(aw, {
      baseFrequency: v => (aw.baseFrequency = v),
      octaves: v => (aw.octaves = v),
      sensitivity: v => (aw.sensitivity = v),
      wet: v => (aw.wet.value = v),
    })
  },
}

const phaserPlugin: EffectPlugin = {
  id: 'tone-phaser',
  label: 'Phaser',
  category: 'modulation',
  defaultPreset: { frequency: 0.5, octaves: 3, baseFrequency: 350, wet: 0.5 },
  paramSchema: [
    {
      id: 'frequency',
      label: 'Rate',
      kind: 'slider',
      min: 0.05,
      max: 10,
      step: 0.05,
      default: 0.5,
    },
    {
      id: 'octaves',
      label: 'Octaves',
      kind: 'slider',
      min: 0,
      max: 6,
      step: 0.1,
      default: 3,
    },
    {
      id: 'baseFrequency',
      label: 'Base freq',
      kind: 'freq',
      min: 20,
      max: 4000,
      step: 10,
      default: 350,
    },
    {
      id: 'wet',
      label: 'Wet',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
    },
  ],
  createNode: (ctx, preset = {}) => {
    registerToneContext(ctx)
    const ph = new Phaser({
      frequency: preset.frequency ?? 0.5,
      octaves: preset.octaves ?? 3,
      baseFrequency: preset.baseFrequency ?? 350,
    })
    ph.wet.value = preset.wet ?? 0.5
    return wrapToneEffect(ph, {
      frequency: v => (ph.frequency.value = v),
      octaves: v => (ph.octaves = v),
      baseFrequency: v => (ph.baseFrequency = v),
      wet: v => (ph.wet.value = v),
    })
  },
}

const tremoloPlugin: EffectPlugin = {
  id: 'tone-tremolo',
  label: 'Tremolo',
  category: 'modulation',
  defaultPreset: { frequency: 10, depth: 0.5, wet: 0.8 },
  paramSchema: [
    {
      id: 'frequency',
      label: 'Rate',
      kind: 'slider',
      min: 0.1,
      max: 20,
      step: 0.1,
      default: 10,
    },
    {
      id: 'depth',
      label: 'Depth',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
    },
    {
      id: 'wet',
      label: 'Wet',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.8,
    },
  ],
  createNode: (ctx, preset = {}) => {
    registerToneContext(ctx)
    const tr = new Tremolo({
      frequency: preset.frequency ?? 10,
      depth: preset.depth ?? 0.5,
    }).start()
    tr.wet.value = preset.wet ?? 0.8
    return wrapToneEffect(tr, {
      frequency: v => (tr.frequency.value = v),
      depth: v => (tr.depth.value = v),
      wet: v => (tr.wet.value = v),
    })
  },
}

const vibratoPlugin: EffectPlugin = {
  id: 'tone-vibrato',
  label: 'Vibrato',
  category: 'modulation',
  defaultPreset: { frequency: 5, depth: 0.1, wet: 1 },
  paramSchema: [
    {
      id: 'frequency',
      label: 'Rate',
      kind: 'slider',
      min: 0.1,
      max: 20,
      step: 0.1,
      default: 5,
    },
    {
      id: 'depth',
      label: 'Depth',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.1,
    },
    {
      id: 'wet',
      label: 'Wet',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 1,
    },
  ],
  createNode: (ctx, preset = {}) => {
    registerToneContext(ctx)
    const vb = new Vibrato({
      frequency: preset.frequency ?? 5,
      depth: preset.depth ?? 0.1,
    })
    vb.wet.value = preset.wet ?? 1
    return wrapToneEffect(vb, {
      frequency: v => (vb.frequency.value = v),
      depth: v => (vb.depth.value = v),
      wet: v => (vb.wet.value = v),
    })
  },
}

const pitchShiftPlugin: EffectPlugin = {
  id: 'tone-pitchshift',
  label: 'Pitch Shift',
  category: 'utility',
  defaultPreset: { pitch: 0, windowSize: 0.1, feedback: 0, wet: 1 },
  paramSchema: [
    {
      id: 'pitch',
      label: 'Pitch (st)',
      kind: 'slider',
      min: -24,
      max: 24,
      step: 1,
      default: 0,
    },
    {
      id: 'windowSize',
      label: 'Window',
      kind: 'time',
      min: 0.03,
      max: 0.5,
      step: 0.01,
      default: 0.1,
    },
    {
      id: 'feedback',
      label: 'Feedback',
      kind: 'slider',
      min: 0,
      max: 0.95,
      step: 0.01,
      default: 0,
    },
    {
      id: 'wet',
      label: 'Wet',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 1,
    },
  ],
  createNode: (ctx, preset = {}) => {
    registerToneContext(ctx)
    const ps = new PitchShift({
      pitch: preset.pitch ?? 0,
      windowSize: preset.windowSize ?? 0.1,
      feedback: preset.feedback ?? 0,
    })
    ps.wet.value = preset.wet ?? 1
    return wrapToneEffect(ps, {
      pitch: v => (ps.pitch = v),
      windowSize: v => (ps.windowSize = v),
      feedback: v => (ps.feedback.value = v),
      wet: v => (ps.wet.value = v),
    })
  },
}

const stereoWidenerPlugin: EffectPlugin = {
  id: 'tone-stereowidener',
  label: 'Stereo Widener',
  category: 'utility',
  defaultPreset: { width: 0.5, wet: 1 },
  paramSchema: [
    {
      id: 'width',
      label: 'Width',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
    },
    {
      id: 'wet',
      label: 'Wet',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 1,
    },
  ],
  createNode: (ctx, preset = {}) => {
    registerToneContext(ctx)
    const sw = new StereoWidener(preset.width ?? 0.5)
    sw.wet.value = preset.wet ?? 1
    return wrapToneEffect(sw, {
      width: v => (sw.width.value = v),
      wet: v => (sw.wet.value = v),
    })
  },
}

/** Wire a Tone node into a generic `PluginHandle`. Gain bridges on
 *  each side present clean AudioNodes to the caller without leaking
 *  Tone types into the plugin interface. */
function wrapToneEffect(
  node: ToneAudioNode,
  setters: Record<string, (value: number) => void>,
): PluginHandle {
  const input = new ToneGain({ context: node.context })
  const output = new ToneGain({ context: node.context })
  input.connect(node)
  node.connect(output)

  return {
    input: input.output as unknown as AudioNode,
    output: output.output as unknown as AudioNode,
    setParam(name, value) {
      const setter = setters[name]
      if (setter) setter(value)
    },
    dispose() {
      try {
        input.dispose()
        node.dispose()
        output.dispose()
      } catch {
        /* already disposed */
      }
    },
  }
}
